import type { CrmProvider } from '@prisma/client';
import { trpc } from '@/code/lib/utils/trpc';

export interface CrmSurface {
  /**
   * The CRM *section* may render: the org is CRM-enabled (`Organization.crmEnabled`)
   * and the SHOULD_RENDER_CRM_FEATURES flag is on. This is the gate for surfaces
   * whose job is to set CRM up — nav entry, settings, records, onboarding step.
   * It is NOT a statement that anything syncs.
   */
  canConfigure: boolean;
  /**
   * Sync is live and writes will actually land. The gate for every CRM surface
   * outside the settings area.
   */
  isLive: boolean;
  /** `isLive` and Aurium writes Opportunities itself (not the customer's orchestration). */
  canManageDeals: boolean;
  /** The org's CRM provider, or null unless sync is live. */
  provider: CrmProvider | null;
  isLoading: boolean;
}

/**
 * The single gate for customer-facing CRM UI.
 *
 * Two levels, because "CRM is set up" and "CRM syncs" are different facts and
 * conflating them is how this went wrong: a `CrmSyncConfig` row is created the
 * moment setup starts, so gating on its presence showed Ramp a Deal Pipeline
 * picker and a force-sync button for months with every leg of the real gate shut.
 *
 * `isLive` mirrors the server's own `CrmSyncConfigService.evaluateSyncGate` (via
 * `getSyncConfig().syncActive`) rather than re-deriving it, so UI can only hide
 * or show on exactly the condition that decides whether a write is a no-op.
 *
 * Both levels report false while loading — hide CRM rather than flash it on.
 */
export function useCrmSurface(): CrmSurface {
  const org = trpc.organizations.getOrganization.useQuery();
  const flag = trpc.featureFlags.getIsFlagEnabledForUser.useQuery(
    'SHOULD_RENDER_CRM_FEATURES',
  );

  const gateLoading = org.isLoading || flag.isLoading;
  const canConfigure =
    !gateLoading && !!org.data?.crmEnabled && flag.data === true;

  // Orgs that can't even configure CRM never pay for this roundtrip.
  const config = trpc.crmConfig.getSyncConfig.useQuery(undefined, {
    enabled: canConfigure,
  });

  const isLive = canConfigure && !!config.data?.syncActive;

  return {
    canConfigure,
    isLive,
    canManageDeals:
      isLive && !!config.data?.syncDeals && !!config.data?.createOpportunities,
    provider: isLive ? (config.data?.provider ?? null) : null,
    isLoading: gateLoading || (canConfigure && config.isLoading),
  };
}
